import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Field, SimpleSelect } from "@/components/common";
import { Belt } from "@/components/Belt";
import { api } from "@/lib/api";
import { act } from "@/lib/useApi";
import { brl, dt, dtTime, todayISO, METHOD_LABEL, CHANNEL_LABEL } from "@/lib/fmt";
import { LogoMark } from "@/components/Logo";

function Shell({ open, onClose, title, children, testid }) {
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid={testid}>
        <DialogHeader><DialogTitle className="font-display text-2xl uppercase">{title}</DialogTitle></DialogHeader>
        {children}
      </DialogContent>
    </Dialog>
  );
}

const StudentPick = ({ students, value, onChange }) => (
  <Field label="Aluno *"><SimpleSelect value={value} onChange={onChange} testid="dlg-student-select"
    options={(students || []).map((s) => ({ value: s.id, label: s.full_name }))} /></Field>
);

export function PromoteDialog({ open, onClose, student, cfg, events = [], onDone }) {
  const kids = student?.belt_system === "infantil";
  const belts = cfg ? (kids ? [...cfg.kids_belts, "Azul"] : cfg.adult_belts) : [];
  const [f, setF] = useState({ belt: student?.next?.belt || student?.belt || "Branca", grau: student?.next?.grau ?? 0, date: todayISO(), notes: "", event_id: "" });
  const save = async () => {
    const r = await act(() => api.post("/graduation/promote", { student_id: student.id, ...f, grau: Number(f.grau), event_id: f.event_id || null }), "Graduação registrada");
    if (r) { onDone?.(r); onClose(); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Registrar graduação" testid="promote-dialog">
      <div className="space-y-3">
        <div className="text-sm">Aluno: <b>{student?.full_name}</b> · atual: {student?.belt} {student?.grau}º grau</div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nova faixa"><SimpleSelect value={f.belt} onChange={(v) => setF({ ...f, belt: v })} testid="promote-belt-select" options={belts.map((b) => ({ value: b, label: b }))} /></Field>
          <Field label="Grau"><Input type="number" min={0} max={6} value={f.grau} onChange={(e) => setF({ ...f, grau: e.target.value })} data-testid="promote-grau-input" /></Field>
          <Field label="Data"><Input type="date" value={f.date} max={todayISO()} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="promote-date-input" /></Field>
          <Field label="Evento / cerimônia"><SimpleSelect value={f.event_id} onChange={(v) => setF({ ...f, event_id: v })} testid="promote-event-select" placeholder="Nenhum"
            options={events.map((e) => ({ value: e.id, label: `${e.name} (${dt(e.date)})` }))} /></Field>
        </div>
        <div className="py-2"><Belt belt={f.belt} grau={Number(f.grau)} size="lg" /></div>
        <Field label="Observações sobre a evolução técnica"><Textarea value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="promote-notes-input" /></Field>
        <Button onClick={save} className="w-full bg-red-600 hover:bg-red-700" data-testid="promote-submit-btn">Confirmar graduação</Button>
      </div>
    </Shell>
  );
}

export function ChargeDialog({ open, onClose, studentId, students, onDone }) {
  const [f, setF] = useState({ student_id: studentId || "", amount: "", due_date: todayISO(), description: "", kind: "avulso" });
  const save = async () => {
    const r = await act(() => api.post("/payments/charges", { ...f, amount: Number(f.amount) }), "Cobrança criada");
    if (r) { onDone?.(); onClose(); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Nova cobrança" testid="charge-dialog">
      <div className="space-y-3">
        {!studentId && <StudentPick students={students} value={f.student_id} onChange={(v) => setF({ ...f, student_id: v })} />}
        <Field label="Descrição *"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="charge-desc-input" /></Field>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Valor (R$) *"><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="charge-amount-input" /></Field>
          <Field label="Vencimento"><Input type="date" value={f.due_date} onChange={(e) => setF({ ...f, due_date: e.target.value })} data-testid="charge-due-input" /></Field>
          <Field label="Tipo"><SimpleSelect value={f.kind} onChange={(v) => setF({ ...f, kind: v })} testid="charge-kind-select"
            options={[["mensalidade", "Mensalidade"], ["avulso", "Avulso"], ["matricula", "Matrícula"], ["produto", "Produto"]].map(([value, label]) => ({ value, label }))} /></Field>
        </div>
        <p className="text-xs text-slate-500">A cobrança fica pendente até o pagamento ser confirmado pelo Stripe ou registrado pela administração.</p>
        <Button onClick={save} className="w-full bg-red-600 hover:bg-red-700" data-testid="charge-submit-btn">Criar cobrança</Button>
      </div>
    </Shell>
  );
}

export function ManualPayDialog({ open, onClose, payment, studentId, students, onDone }) {
  const [f, setF] = useState({ student_id: payment?.student_id || studentId || "", amount: payment?.amount || "", method: "pix", paid_at: todayISO(),
    description: payment?.description || "Pagamento registrado na academia", notes: "" });
  const save = async () => {
    const r = await act(() => api.post("/payments/manual", { ...f, amount: Number(f.amount), payment_id: payment?.id || null }), "Pagamento registrado");
    if (r) { onDone?.(); onClose(); }
  };
  return (
    <Shell open={open} onClose={onClose} title="Registrar recebimento" testid="manual-pay-dialog">
      <div className="space-y-3">
        {payment ? <div className="text-sm rounded-md bg-slate-50 p-3">Quitando: <b>{payment.description}</b> · venc. {dt(payment.due_date)}</div>
          : !studentId && <StudentPick students={students} value={f.student_id} onChange={(v) => setF({ ...f, student_id: v })} />}
        {!payment && <Field label="Descrição"><Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="manual-desc-input" /></Field>}
        <div className="grid grid-cols-3 gap-3">
          <Field label="Valor (R$)"><Input type="number" step="0.01" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="manual-amount-input" /></Field>
          <Field label="Forma"><SimpleSelect value={f.method} onChange={(v) => setF({ ...f, method: v })} testid="manual-method-select"
            options={["pix", "dinheiro", "cartao_maquina", "transferencia"].map((v) => ({ value: v, label: METHOD_LABEL[v] }))} /></Field>
          <Field label="Data"><Input type="date" max={todayISO()} value={f.paid_at} onChange={(e) => setF({ ...f, paid_at: e.target.value })} data-testid="manual-date-input" /></Field>
        </div>
        <Field label="Observações"><Input value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} data-testid="manual-notes-input" /></Field>
        <p className="text-xs text-slate-500">O lançamento será registrado em seu nome na auditoria financeira.</p>
        <Button onClick={save} className="w-full bg-red-600 hover:bg-red-700" data-testid="manual-submit-btn">Confirmar recebimento</Button>
      </div>
    </Shell>
  );
}

export function ReceiptDialog({ paymentId, onClose }) {
  const [r, setR] = useState(null);
  useEffect(() => {
    api.get(`/payments/${paymentId}/receipt`).then((x) => setR(x.data)).catch(() => onClose());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentId]);
  return (
    <Shell open={!!paymentId} onClose={onClose} title="Recibo" testid="receipt-dialog">
      {r && (
        <div className="space-y-4">
          <div className="print-area border border-slate-200 rounded-lg p-5 space-y-3 text-sm">
            <div className="flex items-center gap-2"><LogoMark className="w-12 h-12" /><b className="font-display text-lg">AUGUSTO MACIEL TEAM</b></div>
            <div className="text-xs text-slate-500">Recibo nº {r.payment.id.slice(0, 8).toUpperCase()}</div>
            <div>Recebemos de <b>{r.student.full_name}</b> (CPF {r.student.cpf}) o valor de <b>{brl(r.payment.amount)}</b> referente a <b>{r.payment.description}</b>.</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>Forma: {METHOD_LABEL[r.payment.method] || r.payment.method}</div>
              <div>Canal: {CHANNEL_LABEL[r.payment.channel] || "—"}</div>
              <div>Pago em: {r.payment.paid_at?.length > 10 ? dtTime(r.payment.paid_at) : dt(r.payment.paid_at)}</div>
              <div>Situação: {r.payment.status}</div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => window.print()} className="flex-1 bg-zinc-900" data-testid="receipt-print-btn">Imprimir / PDF</Button>
            {r.payment.receipt_url && <Button asChild variant="outline"><a href={r.payment.receipt_url} target="_blank" rel="noreferrer">Fatura Stripe</a></Button>}
          </div>
        </div>
      )}
    </Shell>
  );
}

export async function openCheckout(paymentId, newTab = false) {
  const r = await act(() => api.post("/payments/checkout", { payment_id: paymentId, origin_url: window.location.origin }));
  if (!r) return;
  if (newTab) {
    await navigator.clipboard?.writeText(r.checkout_url).catch(() => {});
    window.open(r.checkout_url, "_blank");
  } else window.location.href = r.checkout_url;
}

export async function startSubscription(studentId, planId, newTab = false) {
  const r = await act(() => api.post("/payments/subscribe", { student_id: studentId, plan_id: planId, origin_url: window.location.origin }));
  if (!r) return;
  if (newTab) window.open(r.checkout_url, "_blank");
  else window.location.href = r.checkout_url;
}
